import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  CreditCard,
  CheckCircle,
  Clock,
  Loader2,
  Wallet,
  AlertTriangle,
  Calendar, // Tambahkan import ikon Calendar
} from "lucide-react";

export default function TagihanSiswa() {
  const [tagihanList, setTagihanList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [studentInfo, setStudentInfo] = useState(null);

  // STATE DIAGNOSA
  const [debugInfo, setDebugInfo] = useState({
    loginSource: "Mencari di LocalStorage...",
    namaDiLocal: "-",
    statusSiswa: "-",
    errorMsg: "",
  });

  useEffect(() => {
    fetchTagihanSaya();
  }, []);

  const formatRupiah = (number) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(number);
  };

  // FUNGSI BARU: Format Tanggal Indonesia
  const formatTanggal = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  async function fetchTagihanSaya() {
    setLoading(true);
    try {
      // 1. AMBIL DARI LOCAL STORAGE
      const localUserString = localStorage.getItem("ruangKelasUser");

      if (!localUserString) {
        setDebugInfo((prev) => ({
          ...prev,
          errorMsg: "Tidak ditemukan data 'ruangKelasUser' di browser.",
        }));
        setLoading(false);
        return;
      }

      const localUser = JSON.parse(localUserString);
      setDebugInfo((prev) => ({
        ...prev,
        loginSource: "Local Storage OK",
        namaDiLocal: localUser.nama,
      }));

      // 2. CARI DATA SISWA
      const { data: siswa, error: errSiswa } = await supabase
        .from("siswa")
        .select("id, nama_lengkap, kelas")
        .ilike("nama_lengkap", localUser.nama)
        .maybeSingle();

      if (errSiswa) {
        setDebugInfo((prev) => ({
          ...prev,
          errorMsg: "Error Database Siswa: " + errSiswa.message,
        }));
        setLoading(false);
        return;
      }

      if (siswa) {
        setStudentInfo(siswa);
        setDebugInfo((prev) => ({
          ...prev,
          statusSiswa: `Ditemukan (ID: ${siswa.id})`,
        }));

        // 3. AMBIL TAGIHAN
        // select("*") otomatis mengambil kolom tanggal_bayar jika ada di tabel
        const { data: tagihan, error: errTagihan } = await supabase
          .from("tagihan")
          .select("*")
          .eq("siswa_id", siswa.id)
          .order("id", { ascending: false });

        if (errTagihan) throw errTagihan;
        setTagihanList(tagihan || []);
      } else {
        setDebugInfo((prev) => ({
          ...prev,
          statusSiswa: "TIDAK KETEMU",
          errorMsg: `Tidak ada siswa di database dengan nama persis: "${localUser.nama}"`,
        }));
      }
    } catch (error) {
      console.error("Global Error:", error);
      setDebugInfo((prev) => ({ ...prev, errorMsg: error.message }));
    } finally {
      setLoading(false);
    }
  }

  const getStatusColor = (status) => {
    const s = status ? status.toLowerCase() : "";
    if (s === "lunas")
      return {
        bg: "#dcfce7",
        text: "#166534",
        icon: <CheckCircle size={16} />,
      };
    return { bg: "#fee2e2", text: "#991b1b", icon: <Clock size={16} /> };
  };

  const totalTunggakan = tagihanList
    .filter((t) => t.status !== "LUNAS")
    .reduce((acc, curr) => acc + (curr.jumlah || 0), 0);

  if (loading)
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          background: "#f8fafc",
        }}
      >
        <Loader2 className="animate-spin" size={40} color="#2563eb" />
        <p style={{ marginTop: 15, color: "#64748b" }}>
          Memuat Data Keuangan...
        </p>
      </div>
    );

  return (
    <div
      style={{
        maxWidth: "500px",
        margin: "0 auto",
        padding: "20px",
        fontFamily: "sans-serif",
        background: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      <Link
        to="/"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "5px",
          textDecoration: "none",
          color: "#64748b",
          marginBottom: "20px",
        }}
      >
        <ArrowLeft size={18} /> Kembali ke Dashboard
      </Link>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 20,
        }}
      >
        <div>
          <h2 style={{ margin: 0, color: "#1e293b", fontSize: "22px" }}>
            Tagihan Saya
          </h2>
          <p
            style={{ margin: "5px 0 0 0", color: "#64748b", fontSize: "13px" }}
          >
            Halo,{" "}
            <strong>
              {studentInfo?.nama_lengkap || debugInfo.namaDiLocal}
            </strong>
          </p>
        </div>
        <div
          style={{
            width: 45,
            height: 45,
            background: "#eff6ff",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#2563eb",
          }}
        >
          <Wallet size={24} />
        </div>
      </div>

      {/* CARD RINGKASAN */}
      <div
        style={{
          background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
          borderRadius: "16px",
          padding: "20px",
          color: "white",
          boxShadow: "0 10px 25px -5px rgba(37, 99, 235, 0.4)",
          marginBottom: "25px",
        }}
      >
        <div style={{ fontSize: "13px", opacity: 0.9, marginBottom: "5px" }}>
          Total Tunggakan (Belum Lunas)
        </div>
        <div style={{ fontSize: "28px", fontWeight: "bold" }}>
          {formatRupiah(totalTunggakan)}
        </div>
        <div
          style={{
            fontSize: "12px",
            marginTop: "10px",
            background: "rgba(255,255,255,0.2)",
            display: "inline-block",
            padding: "4px 10px",
            borderRadius: "20px",
          }}
        >
          {tagihanList.length} Riwayat Tagihan
        </div>
      </div>

      {/* LIST TAGIHAN */}
      <h3 style={{ color: "#334155", fontSize: "16px", marginBottom: "15px" }}>
        Riwayat Pembayaran
      </h3>

      {tagihanList.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            background: "white",
            borderRadius: "12px",
            border: "1px dashed #cbd5e1",
          }}
        >
          <CheckCircle size={40} color="#cbd5e1" style={{ marginBottom: 10 }} />
          <p style={{ color: "#94a3b8" }}>Tidak ada data tagihan ditemukan.</p>
        </div>
      ) : (
        <div style={{ display: "grid", gap: "12px" }}>
          {tagihanList.map((item) => {
            const statusStyle = getStatusColor(item.status);
            return (
              <div
                key={item.id}
                style={{
                  background: "white",
                  padding: "16px",
                  borderRadius: "16px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                  border: "1px solid #f1f5f9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 15 }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: "12px",
                      background: "#f8fafc",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#64748b",
                    }}
                  >
                    <CreditCard size={20} />
                  </div>
                  <div>
                    <div
                      style={{
                        fontWeight: "bold",
                        color: "#1e293b",
                        fontSize: "14px",
                      }}
                    >
                      {item.judul_tagihan}
                    </div>

                    {/* TAMPILKAN JATUH TEMPO (Jika Ada) */}
                    {item.jatuh_tempo && (
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#dc2626",
                          marginTop: 2,
                        }}
                      >
                        Tempo: {item.jatuh_tempo}
                      </div>
                    )}

                    {/* TAMPILKAN TANGGAL BAYAR (Jika Sudah Dibayar) */}
                    {item.tanggal_bayar && (
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#166534", // Warna hijau tua
                          marginTop: 2,
                          fontWeight: "500",
                          display: "flex",
                          alignItems: "center",
                          gap: 3,
                        }}
                      >
                        <Calendar size={10} />
                        Lunas: {formatTanggal(item.tanggal_bayar)}
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ textAlign: "right" }}>
                  <div
                    style={{
                      fontWeight: "bold",
                      color: "#1e293b",
                      fontSize: "14px",
                    }}
                  >
                    {formatRupiah(item.jumlah)}
                  </div>
                  <span
                    style={{
                      background: statusStyle.bg,
                      color: statusStyle.text,
                      fontSize: "10px",
                      fontWeight: "bold",
                      padding: "3px 8px",
                      borderRadius: "20px",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 3,
                      marginTop: 4,
                    }}
                  >
                    {statusStyle.icon} {item.status}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
